import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { FiArrowLeft, FiTarget, FiUsers, FiStar, FiAward, FiMapPin } from 'react-icons/fi';
import TimeRangeSelector from '../components/TimeRangeSelector';
import ComparisonView from '../components/ComparisonView';
import { useTranslation } from '../hooks/useTranslation';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const ChallengeStatistics = () => {
  const { t } = useTranslation();
  const { challengeId } = useParams();
  const navigate = useNavigate();

  const [timeRange, setTimeRange] = useState({ type: 'preset', days: '30' });
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonType, setComparisonType] = useState('previous');
  const [comparisonRange, setComparisonRange] = useState(() => {
    // Default: previous month (30 days ending yesterday)
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1); // Yesterday
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 31); // 31 days ago
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  });
  const [comparisonViewMode, setComparisonViewMode] = useState('side-by-side');
  const [selectedMetrics, setSelectedMetrics] = useState(['completions', 'visitors']);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cms_accessToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Build query params
  const getQueryParams = () => {
    if (timeRange.type === 'custom' || timeRange.type === 'month') {
      return `startDate=${timeRange.startDate}&endDate=${timeRange.endDate}`;
    }
    return `days=${timeRange.days}`;
  };

  // Fetch challenge stats
  const { data: statsData, isLoading } = useQuery(
    ['challengeStats', challengeId, timeRange],
    () => axios.get(`${API_URL}/analytics/challenge/${challengeId}?${getQueryParams()}`, {
      headers: getAuthHeaders()
    }).then(res => res.data)
  );

  // Fetch comparison data if in comparison mode
  const { data: comparisonData, isLoading: loadingComparison } = useQuery(
    ['challengeComparison', challengeId, timeRange, comparisonRange],
    () => {
      // Period 1 is the current selection
      let p1Start, p1End;
      if (timeRange.type === 'custom' || timeRange.type === 'month') {
        p1Start = timeRange.startDate;
        p1End = timeRange.endDate;
      } else if (timeRange.days === '0' || timeRange.days === 0) {
        // "Forever" - use all time from 2020 to today
        p1End = new Date().toISOString().split('T')[0];
        p1Start = '2020-01-01';
      } else {
        p1End = new Date().toISOString().split('T')[0];
        p1Start = new Date(Date.now() - parseInt(timeRange.days) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      }

      return axios.get(
        `${API_URL}/analytics/compare?challengeId=${challengeId}&period1Start=${p1Start}&period1End=${p1End}&period2Start=${comparisonRange.startDate}&period2End=${comparisonRange.endDate}`,
        { headers: getAuthHeaders() }
      ).then(res => res.data);
    },
    { enabled: comparisonMode && !!comparisonRange.startDate && !!comparisonRange.endDate }
  );

  // Chart colors
  const COLORS = ['#11827E', '#C53B8A', '#1ab7b1', '#e845a0'];
  const NFT_RARITY_COLORS = { common: '#9ca3af', uncommon: '#22c55e', rare: '#a855f7', epic: '#f59e0b', legendary: '#ef4444' };

  const challenge = statsData?.challenge;
  const summary = statsData?.summary;

  // Format period labels for comparison
  const getPeriod1Label = () => {
    if (timeRange.type === 'custom' || timeRange.type === 'month') {
      const start = new Date(timeRange.startDate);
      const end = new Date(timeRange.endDate);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return timeRange.days === '0' ? 'All Time' : `Last ${timeRange.days} Days`;
  };

  const getPeriod2Label = () => {
    if (!comparisonRange.startDate || !comparisonRange.endDate) return '';
    const start = new Date(comparisonRange.startDate);
    const end = new Date(comparisonRange.endDate);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Challenge not found</p>
        <button
          onClick={() => navigate('/statistics')}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          {t('cms.common.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/statistics')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-primary-600">{challenge.name}</h1>
            <p className="text-gray-600">
              {challenge.type && <span className="capitalize">{challenge.type}</span>}
              {challenge.difficulty && <span className="ml-2 text-gray-400">| {challenge.difficulty}</span>}
              {challenge.points && <span className="ml-2 text-gray-400">| {challenge.points} pts</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <TimeRangeSelector
          value={timeRange}
          onChange={setTimeRange}
          comparisonMode={comparisonMode}
          onComparisonModeChange={setComparisonMode}
          comparisonValue={comparisonRange}
          onComparisonChange={setComparisonRange}
          comparisonType={comparisonType}
          onComparisonTypeChange={setComparisonType}
        />
      </div>

      {/* Comparison View */}
      {comparisonMode && comparisonData && (
        <ComparisonView
          period1Data={comparisonData.period1}
          period2Data={comparisonData.period2}
          period1Label={getPeriod1Label()}
          period2Label={getPeriod2Label()}
          viewMode={comparisonViewMode}
          onViewModeChange={setComparisonViewMode}
          selectedMetrics={selectedMetrics}
          onMetricsChange={setSelectedMetrics}
          deltas={comparisonData.deltas}
        />
      )}

      {/* Regular Stats View (when not in comparison mode) */}
      {!comparisonMode && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Completions</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{summary?.totalCompletions || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
                  <FiTarget className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unique Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{summary?.uniqueUsers || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                  <FiUsers className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Average Rating</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {summary?.avgRating || 0}
                    <span className="text-lg text-gray-400">/5</span>
                  </p>
                  <p className="text-xs text-gray-500">{summary?.ratingCount || 0} reviews</p>
                </div>
                <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-500 to-yellow-600">
                  <FiStar className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">NFTs Minted</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">{summary?.nftsMinted || 0}</p>
                  <p className="text-xs text-gray-500">
                    {summary?.totalCompletions > 0
                      ? `${((summary?.nftsMinted || 0) / summary.totalCompletions * 100).toFixed(0)}% mint rate`
                      : '0% mint rate'
                    }
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                  <FiAward className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>

          {/* Completions Over Time */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Completions Over Time</h2>
            {(statsData?.completionsOverTime || []).length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-400">
                No completion data for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={statsData?.completionsOverTime || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="completions" stroke="#11827E" strokeWidth={2} fill="#11827E" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Demographics & Ratings */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* User Demographics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">User Demographics</h2>
              {(statsData?.demographics || []).length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No demographic data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={statsData?.demographics || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {(statsData?.demographics || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Ratings Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Rating Distribution</h2>
              {(statsData?.ratings || []).every(r => r.count === 0) ? (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No ratings yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={statsData?.ratings || []} layout="vertical" margin={{ left: 40, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="stars" tickFormatter={(v) => `${v} star${v > 1 ? 's' : ''}`} />
                    <Tooltip formatter={(value) => [value, 'Ratings']} />
                    <Bar dataKey="count" fill="#eab308" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* NFT Stats & Peak Times */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* NFT Stats by Rarity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">NFT Stats by Rarity</h2>
              {(statsData?.nftStats || []).length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No NFT data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={statsData?.nftStats || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="rarity" tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="earned" fill="#11827E" name="Earned" />
                    <Bar dataKey="minted" fill="#C53B8A" name="Minted" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Peak Hours */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Peak Activity Hours</h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={statsData?.peakHours || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="activity" stroke="#11827E" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Peak Days */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity by Day of Week</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={statsData?.peakDays || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="activity" fill="#C53B8A" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Geographic Heatmap */}
          {(statsData?.geographic || []).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Completion Locations</h2>
              <div className="h-96 rounded-lg overflow-hidden">
                <MapContainer
                  center={[
                    statsData.geographic[0]?.latitude || challenge.latitude || 52.20,
                    statsData.geographic[0]?.longitude || challenge.longitude || 12.82
                  ]}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {/* Challenge location marker */}
                  {challenge.latitude && challenge.longitude && (
                    <CircleMarker
                      center={[challenge.latitude, challenge.longitude]}
                      radius={12}
                      fillColor="#ef4444"
                      color="#fff"
                      weight={3}
                      opacity={1}
                      fillOpacity={0.9}
                    >
                      <Popup>
                        <strong>Challenge Location</strong>
                        <br />
                        {challenge.name}
                      </Popup>
                    </CircleMarker>
                  )}
                  {/* Completion locations */}
                  {statsData.geographic.map((point, index) => (
                    <CircleMarker
                      key={index}
                      center={[point.latitude, point.longitude]}
                      radius={8}
                      fillColor="#11827E"
                      color="#fff"
                      weight={2}
                      opacity={0.9}
                      fillOpacity={0.7}
                    >
                      <Popup>
                        <strong>{point.username}</strong>
                        <br />
                        Completed: {new Date(point.completedAt).toLocaleDateString()}
                      </Popup>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ChallengeStatistics;
